import type { ReactNode } from 'react';
import { normalizeGreek } from '@search/shared';
import { greeklishToGreek, looksLikeGreeklish } from '@/lib/greeklishToGreek';

/** Strip trailing sigma + vowels to get a Greek stem for fuzzy matching */
function greekStem(s: string): string {
  return s.replace(/ς$/, '').replace(/[αεηιουω]+$/, '');
}

/** Check if a normalized word matches a query token (handles Greek morphology) */
function stemMatches(normalizedWord: string, token: string): boolean {
  if (normalizedWord.includes(token)) return true;
  const ws = greekStem(normalizedWord);
  const ts = greekStem(token);
  return ws.length >= 3 && ts.length >= 3 && (ws.startsWith(ts) || ts.startsWith(ws));
}

/** Highlight words in `text` whose normalized form matches a query token */
export function highlightTokens(text: string, queryTokens: string[]): ReactNode {
  if (queryTokens.length === 0) return text;

  const words = text.split(/(\s+|[/>])/);
  return words.map((word, i) => {
    if (/^\s+$/.test(word) || word === '/' || word === '>') return word;
    const normalizedWord = normalizeGreek(word);
    const matches = queryTokens.some(token => stemMatches(normalizedWord, token));
    return matches
      ? <span key={i} className="text-highlight">{word}</span>
      : <span key={i}>{word}</span>;
  });
}

/** Extract normalized query tokens (min length 3) from a search query.
 *
 * When the query looks like Greeklish (Latin-typed Greek, e.g. "diakoptis"),
 * also emit the transliterated+normalized tokens so Greek result text in
 * titles, filter labels, and categories gets highlighted. Mirrors how the
 * search route runs a parallel Greeklish query — see lib/greeklishToGreek.ts.
 */
export function getQueryTokens(query: string | undefined): string[] {
  if (!query?.trim()) return [];
  const tokens = normalizeGreek(query).split(/\s+/).filter(t => t.length >= 3);
  if (looksLikeGreeklish(query)) {
    const greekTokens = normalizeGreek(greeklishToGreek(query))
      .split(/\s+/)
      .filter(t => t.length >= 3);
    tokens.push(...greekTokens);
  }
  return tokens;
}
